import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon, CheckIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
const customers = [{
  id: 'cust_1',
  name: 'John Doe',
  email: 'john@example.com',
  cards: [{
    id: 'card_1',
    last4: '4242',
    brand: 'Visa',
    expiry: '04/27'
  }, {
    id: 'card_2',
    last4: '5555',
    brand: 'Mastercard',
    expiry: '07/24',
    isExpired: true
  }, {
    id: 'card_3',
    last4: '9012',
    brand: 'Amex',
    expiry: '11/28'
  }]
}, {
  id: 'cust_2',
  name: 'Jane Smith',
  email: 'jane@example.com',
  cards: [{
    id: 'card_4',
    last4: '1234',
    brand: 'Visa',
    expiry: '09/26'
  }, {
    id: 'card_5',
    last4: '8877',
    brand: 'Mastercard',
    expiry: '03/28'
  }]
}];
const intervalOptions = [{
  value: 'weekly',
  label: 'Weekly',
  days: 7
}, {
  value: 'biweekly',
  label: 'Every 2 weeks',
  days: 14
}, {
  value: 'monthly',
  label: 'Monthly',
  days: 30
}, {
  value: 'quarterly',
  label: 'Quarterly',
  days: 90
}, {
  value: 'biannually',
  label: 'Every 6 months',
  days: 180
}, {
  value: 'annually',
  label: 'Annually',
  days: 365
}];
const cardBrandLogo = (brand: string) => {
  const {
    t
  } = useTranslation('payments');
  const b = brand.toLowerCase();
  if (b === 'visa') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/visa_logo.png" alt={t('components.CardOnFilePayment.attrs.visa')} className="h-4 object-contain" data-id="element-8588" data-mp="10fvavsu1416p" />;
  if (b === 'mastercard') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/mastercard_logo.png" alt={t('components.CardOnFilePayment.attrs.mastercard')} className="h-6 object-contain" data-id="element-8589" data-mp="1f4rfth0sgy33" />;
  if (b === 'amex') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/amex_logo.svg" alt={t('components.CardOnFilePayment.attrs.amex')} className="h-6 object-contain" data-id="element-8590" data-mp="wf1m6x8riicr" />;
  return <span className="text-[10px] font-bold uppercase text-gray-500" data-id="element-8591" data-mp="27w1fnw6uj38d">
      {brand}
    </span>;
};
const formatDateShort = (d: Date) => d.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});
const formatDateFull = (d: Date) => `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
function addInterval(date: Date, interval: string): Date {
  const d = new Date(date);
  switch (interval) {
    case 'weekly':
      d.setDate(d.getDate() + 7);
      break;
    case 'biweekly':
      d.setDate(d.getDate() + 14);
      break;
    case 'monthly':
      d.setMonth(d.getMonth() + 1);
      break;
    case 'quarterly':
      d.setMonth(d.getMonth() + 3);
      break;
    case 'biannually':
      d.setMonth(d.getMonth() + 6);
      break;
    case 'annually':
      d.setFullYear(d.getFullYear() + 1);
      break;
  }
  return d;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [trialDays, setTrialDays] = useState('0');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<(typeof customers)[0] | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Interval dropdown
  const [intervalSearch, setIntervalSearch] = useState('');
  const [intervalOpen, setIntervalOpen] = useState(false);
  const intervalRef = useRef<HTMLDivElement>(null);
  // Date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    onSubscriptionModeChange?.(subscriptionMode);
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (intervalRef.current && !intervalRef.current.contains(e.target as Node)) setIntervalOpen(false);
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^\d.]/g, '');
    setAmount(v);
    onAmountChange(v);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
    const validCards = customer.cards.filter(c => !c.isExpired);
    if (validCards.length > 0) setSelectedCard(validCards[0].id);
  };
  const filteredCustomers = searchTerm ? customers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  const filteredIntervals = intervalSearch ? intervalOptions.filter(o => o.label.toLowerCase().includes(intervalSearch.toLowerCase())) : intervalOptions;
  const selectedIntervalLabel = intervalOptions.find(o => o.value === billingInterval)?.label || '';
  // Calendar generation
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const calendarWeeks = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const last = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const startDay = first.getDay();
    const daysInMonth = last.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return weeks;
  }, [calendarMonth]);
  const isDatePast = (d: Date) => d < today;
  const isDateSelected = (d: Date) => startDate && d.getDate() === startDate.getDate() && d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear();
  const isDateToday = (d: Date) => d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  // Generate billing cycle preview
  const billingPreview = useMemo(() => {
    const cycles: Array<{
      cycle: number;
      date: Date;
      amount: string;
      isTrial: boolean;
    }> = [];
    const effectiveStart = startDate || new Date();
    const trial = parseInt(trialDays) || 0;
    const trialEnd = new Date(effectiveStart);
    trialEnd.setDate(trialEnd.getDate() + trial);
    const maxCycles = billingCycles ? parseInt(billingCycles) : 12;
    const displayCycles = Math.min(maxCycles, 24);
    let currentDate = trial > 0 ? new Date(trialEnd) : new Date(effectiveStart);
    // If there's a trial, add trial entry
    if (trial > 0) {
      cycles.push({
        cycle: 0,
        date: new Date(effectiveStart),
        amount: '0',
        isTrial: true
      });
    }
    for (let i = 0; i < displayCycles; i++) {
      cycles.push({
        cycle: i + 1,
        date: new Date(currentDate),
        amount: amount || '0',
        isTrial: false
      });
      currentDate = addInterval(currentDate, billingInterval);
    }
    return cycles;
  }, [startDate, trialDays, billingCycles, billingInterval, amount]);
  return <div className="space-y-5" data-id="element-8592" data-mp="571fcfqse5ah">
      {/* ─── SUBSCRIPTION TYPE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-8593" data-mp="1lm2zakomlrrl">
        <div className="p-6" data-id="element-8594" data-mp="1gnrp28uf5f8u">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-8595" data-mp="1gokr4wifngkn">
            {t('components.RecurringPayment.subscription_type')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8596" data-mp="1fm5rz1eampau">
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'existing' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('existing')} data-id="element-8597" data-mp="1fp1piom8bbyr">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'existing'} onChange={() => setSubscriptionMode('existing')} data-id="element-8598" data-mp="ww7l2om8f28s" />

              <div data-id="element-8599" data-mp="2ma2y49y70wyp">
                <div className="flex items-center gap-2" data-id="element-8600" data-mp="2mam5iwq7w2yh">
                  <CreditCardIcon size={15} className="text-[#9F140F]" data-id="element-8601" data-mp="2hbjbe7brzh5a" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-8602" data-mp="2ha89kd1w4jgt">
                    {t('components.RecurringPayment.existing_customer')}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-8603" data-mp="2tskbir96wvwy">
                  {t('components.RecurringPayment.charge_a_saved_payment_method_on')}
                </p>
              </div>
            </div>
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'new' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('new')} data-id="element-8604" data-mp="25x7s0ieebx4g">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'new'} onChange={() => setSubscriptionMode('new')} data-id="element-8605" data-mp="2ku8n8vvsqa7o" />

              <div data-id="element-8606" data-mp="1w5ky4h7otn4g">
                <div className="flex items-center gap-2" data-id="element-8607" data-mp="1w51khcp2lkw8">
                  <LinkIcon size={15} className="text-[#9F140F]" data-id="element-8608" data-mp="1r8kx4kpp3ak7" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-8609" data-mp="257kgbcufux8z">
                    {t('components.RecurringPayment.new_subscription_link')}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-8610" data-mp="2144pwy8ypt4q">
                  {t('components.RecurringPayment.send_a_link_to_a_customer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── CUSTOMER (EXISTING) ─── */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-8611" data-mp="1hhgffd8mxblo">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8612" data-mp="1hg5bndf6w3uk">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8613" data-mp="tr2us7sdqf14">
              <UserIcon size={16} className="mr-2 text-[#E2231A]" data-id="element-8614" data-mp="ppr4e01olprv" />
              {t('components.BulkPaymentPreview.customer')}
            </h3>
          </div>

          {!selectedCustomer ? <div className="p-6" data-id="element-8615" data-mp="kdu0jz78qvqr">
              <div className="relative" data-id="element-8616" data-mp="kewru88h7077">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8617" data-mp="5oxxyhnyeaxj">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-8618" data-mp="an96mp2iife6" />
                </div>
                <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.RecurringPayment.attrs.search_by_name_or_email')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-8619" data-mp="ydmoxnvaozvk" />

                {searchTerm && filteredCustomers.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-8620" data-mp="394d3b5ldzwcc">
                    {filteredCustomers.map((customer, idx) => <button key={customer.id} type="button" onClick={() => handleSelectCustomer(customer)} className={`w-full px-4 py-3 text-left flex items-center hover:bg-gray-50 transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-8621" data-mp="3ucp6jaff86ca" data-mpi={idx}>
                        <div className="h-8 w-8 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-8622" data-mp="h5dljxjuyc5i">
                          <UserIcon size={14} className="text-[#9F140F]" data-id="element-8623" data-mp="c6smw2k980j6" />
                        </div>
                        <div data-id="element-8624" data-mp="753c8o0y1ovm">
                          <p className="text-sm font-medium text-gray-900" data-id="element-8625" data-mp="27uz51unkb18">
                            {customer.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-8626" data-mp="2rxygyii3ogl3">
                            {customer.email}
                          </p>
                        </div>
                      </button>)}
                  </div>}
              </div>
            </div> : <>
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200" data-id="element-8627" data-mp="17d1ksaqy3its">
                <div className="flex items-center min-w-0" data-id="element-8628" data-mp="1ccear4d4560l">
                  <div className="h-9 w-9 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-8629" data-mp="1h5pid7pr1qtk">
                    <UserIcon size={16} className="text-[#9F140F]" data-id="element-8630" data-mp="1m63hu1dabsfz" />
                  </div>
                  <div className="min-w-0" data-id="element-8631" data-mp="1q3suxeirbd98">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-8632" data-mp="1v473cxo9sp7s">
                      {selectedCustomer.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-8633" data-mp="2030hmsbdsk90">
                      {selectedCustomer.email}
                    </p>
                  </div>
                </div>
                <button onClick={() => {
            setSelectedCustomer(null);
            setSelectedCard(null);
          }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors flex-shrink-0 ml-4" data-id="element-8634" data-mp="5qttyu1awazt">
                  {t('components.CardOnFilePayment.change')}
                </button>
              </div>

              <div data-id="element-8635" data-mp="2ml09lbnm9k6">
                {selectedCustomer.cards.map((card, idx) => {
            const expired = !!(card as any).isExpired;
            return <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${expired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCard === card.id ? 'bg-[#E2231A]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
              if (!expired) setSelectedCard(card.id);
            }} data-id="element-8636" data-mp="cn550xydtw75" data-mpi={idx}>
                      <input type="radio" name="recurringCard" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedCard === card.id} disabled={expired} onChange={() => {
                if (!expired) setSelectedCard(card.id);
              }} data-id="element-8637" data-mp="1ez4x3tztyxgl" />

                      <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-8638" data-mp="102e2rz50kbxu">
                        <div className="flex items-center" data-id="element-8639" data-mp="maqypd4txz4i">
                          <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${expired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-8640" data-mp="m84sphxl7roe">
                            {cardBrandLogo(card.brand)}
                          </div>
                          <div data-id="element-8641" data-mp="wt5syovuxynd">
                            <p className={`text-sm font-medium ${expired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-8642" data-mp="ws35kqxquec7">
                              {card.brand} •••• {card.last4}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-8643" data-mp="itwi84qf7wqs">
                              <p className={`text-xs ${expired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8644" data-mp="dts06a9v6tyg">
                                {t('components.CardOnFilePayment.expires')}{' '}
                                {card.expiry}
                              </p>
                              {expired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-8645" data-mp="shy0ip6ln7">
                                  {t('components.CardOnFilePayment.expired')}
                                </span>}
                            </div>
                          </div>
                        </div>
                        {!expired && selectedCard === card.id && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-8646" data-mp="3m3jeu30tmvqp" />}
                      </div>
                    </div>;
          })}
              </div>
            </>}
        </div>}

      {/* ─── CUSTOMER INFO (NEW) ─── */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-8647" data-mp="33ctbyeb938xu">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8648" data-mp="384tdgb6yy9wj">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8649" data-mp="3864l4m3jc1sx">
              <UserIcon size={16} className="mr-2 text-[#E2231A]" data-id="element-8650" data-mp="2aonrlso3zlyj" />
              {t('components.FixedLinksPayment.customer_information')}
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-8651" data-mp="brqo5qexbxw3">
              {t('components.RecurringPayment.well_send_a_subscription_link_to')}
            </p>
          </div>
          <div className="p-6 space-y-4" data-id="element-8652" data-mp="lovfe4nmmjjr">
            <div data-id="element-8653" data-mp="ln0s4jovufci">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8654" data-mp="pqqed2i34kud">
                {t('components.FixedLinksPayment.name')}{' '}
                <span className="normal-case text-gray-400" data-id="element-8655" data-mp="3p7eue5hanswm">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <div className="relative" data-id="element-8656" data-mp="20w0chm14q8j">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8657" data-mp="1zunc7lgdto6">
                  <UserIcon size={14} className="text-gray-400" data-id="element-8658" data-mp="6tz0j8lbaxxp" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('components.RecurringPayment.attrs.john_doe')} value={customerName} onChange={e => setCustomerName(e.target.value)} data-id="element-8659" data-mp="36f3zoywsn19l" />
              </div>
            </div>
            <div data-id="element-8660" data-mp="2twi6ktspqbm6">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8661" data-mp="2tvytprhx6df0">
                {t('components.RecurringPayment.email')}{' '}
                <span className="text-red-400" data-id="element-8662" data-mp="2jykh62z4a8rz">*</span>
              </label>
              <div className="relative" data-id="element-8663" data-mp="2k0nfdaafb87s">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8664" data-mp="2f7c5t1e88dc6">
                  <MailIcon size={14} className="text-gray-400" data-id="element-8665" data-mp="2a7zlanatvicr" />
                </div>
                <input type="email" className={`${inputClass} pl-9`} placeholder={t('components.RecurringPayment.attrs.customerexamplecom')} value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} data-id="element-8666" data-mp="267fl1auyjyme" />
              </div>
            </div>
            <div data-id="element-8667" data-mp="1rk755wqpf543">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8668" data-mp="1rkqo9gs3bzjp">
                {t('components.RecurringPayment.phone')}{' '}
                <span className="normal-case text-gray-400" data-id="element-8669" data-mp="1hmb0nns9v8ey">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <div className="relative" data-id="element-8670" data-mp="25cndnd2djvce">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8671" data-mp="2aaoursg8pkl8">
                  <PhoneIcon size={14} className="text-gray-400" data-id="element-8672" data-mp="1vmlqcnhuigyj" />
                </div>
                <input type="tel" className={`${inputClass} pl-9`} placeholder="+66 81 234 5678" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} data-id="element-8673" data-mp="20kvlwz7hfjko" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── SUBSCRIPTION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-8674" data-mp="cne1a7jnoxza">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8675" data-mp="cn4bol2iwsu4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8676" data-mp="rck21zy56w9a">
            <RepeatIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8677" data-mp="mcnujqq8kmoz" />
            {t('components.RecurringPayment.subscription_details')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-8678" data-mp="3uv3j06bdefos">
          <div data-id="element-8679" data-mp="3pufqthbenssz">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8680" data-mp="3puxwt5nvdle3">
              {t('components.RecurringPayment.amount_per_billing_period')}{' '}
              <span className="text-red-400" data-id="element-8681" data-mp="3m6dwkiohhjz">*</span>
            </label>
            <div className="relative" data-id="element-8682" data-mp="1j3gcyv0n6qtx">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8683" data-mp="1j2f0qn7w3yam">
                <span className="text-sm font-medium text-gray-500" data-id="element-8684" data-mp="1f0ti0l8ocpsi">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-8685" data-mp="2ed3fhq06oalh" />
            </div>
          </div>
          <div data-id="element-8686" data-mp="2o9pz6bgrwvlx">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8687" data-mp="2tavsobi9cmwi">
              {t('components.BulkPaymentPreview.description')}{' '}
              <span className="text-red-400" data-id="element-8688" data-mp="2xao5j0eopf7o">*</span>
            </label>
            <input type="text" className={inputClass} placeholder={t('components.RecurringPayment.attrs.eg_premium_membership')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-8689" data-mp="3248u61ue0wu7" />
          </div>
          <div data-id="element-8690" data-mp="3h0q6owebchic">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8691" data-mp="3gy3ugjgh9dts">
              {t('components.BulkPaymentPreview.reference')}{' '}
              <span className="normal-case text-gray-400" data-id="element-8692" data-mp="10cgszg4pxndu">
                {t('components.RecurringPayment.optional')}
              </span>
            </label>
            <input type="text" className={inputClass} placeholder={t('components.RecurringPayment.attrs.internal_reference')} value={reference} onChange={handleReferenceChange} data-id="element-8693" data-mp="qgdnq1ekl8mo" />
          </div>
        </div>
      </div>

      {/* ─── BILLING SCHEDULE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-8694" data-mp="19eq0dx4ox0n0">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8695" data-mp="19e6r14t3vkkl">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8696" data-mp="26udmh91ois8c">
            <ClockIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8697" data-mp="223ngpupccnd4" />
            {t('components.RecurringPayment.billing_schedule')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-8698" data-mp="6n17bxpsmxd">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8699" data-mp="54ok9wv54m8u">
            {/* Searchable interval dropdown */}
            <div ref={intervalRef} className="relative" data-id="element-8700" data-mp="3mm7szjr6h0oq">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8701" data-mp="3mjll1j0dkj5z">
                {t('components.RecurringPayment.billing_interval')}
              </label>
              <button type="button" aria-expanded={intervalOpen} aria-haspopup="listbox" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => {
              setIntervalOpen(!intervalOpen);
              setIntervalSearch('');
            }} data-id="element-8702" data-mp="3dmuicnrbi62f">
                <span className={selectedIntervalLabel ? 'text-gray-900' : 'text-gray-400'} data-id="element-8703" data-mp="11t491qqferkw">
                  {selectedIntervalLabel || 'Select interval'}
                </span>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${intervalOpen ? 'rotate-180' : ''}`} data-id="element-8704" data-mp="2measheb8rie7" />
              </button>

              {intervalOpen && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-8705" data-mp="2w9cg25pvcceo">
                  <div className="p-2 border-b border-gray-100" data-id="element-8706" data-mp="353ziqu45ean8">
                    <div className="relative" data-id="element-8707" data-mp="355ttxgpmaoe4">
                      <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-8708" data-mp="3k1jgph2mn5jw" />

                      <input type="text" className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] placeholder:text-gray-400" placeholder={t('components.RecurringPayment.attrs.search')} value={intervalSearch} onChange={e => setIntervalSearch(e.target.value)} autoFocus data-id="element-8709" data-mp="1pmp9jqsgzkzk" />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto" data-id="element-8710" data-mp="fwnsvqsoj1al">
                    {filteredIntervals.length > 0 ? filteredIntervals.map(opt => <button key={opt.value} type="button" className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${billingInterval === opt.value ? 'bg-[#E2231A]/5 text-[#E2231A] font-medium' : 'text-gray-700'}`} onClick={() => {
                  setBillingInterval(opt.value);
                  setIntervalOpen(false);
                }} data-id="element-8711" data-mp="pua8yglpsyw5">
                          {opt.label}
                          {billingInterval === opt.value && <CheckIcon size={14} className="text-[#9F140F]" data-id="element-8712" data-mp="3gyexc2of5j27" />}
                        </button>) : <div className="px-3 py-4 text-sm text-gray-500 text-center" data-id="element-8713" data-mp="373wqionph7e6">
                        {t('components.RecurringPayment.no_results')}
                      </div>}
                  </div>
                </div>}
            </div>

            <div data-id="element-8714" data-mp="2t3lwjcr3ncos">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8715" data-mp="2t459ef1w7avy">
                {t('components.RecurringPayment.billing_cycles')}{' '}
                <span className="normal-case text-gray-400" data-id="element-8716" data-mp="rc8y96jn031p">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <input type="number" min="1" className={inputClass} placeholder={t('components.RecurringPayment.attrs.unlimited')} value={billingCycles} onChange={e => setBillingCycles(e.target.value)} data-id="element-8717" data-mp="1cm9xtmbt6ux8" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8718" data-mp="ymsh7u8o2nyy">
            {/* Date picker */}
            <div ref={datePickerRef} className="relative" data-id="element-8719" data-mp="toqn8e9pd48p">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8720" data-mp="17ngkpv0fp5xl">
                {t('components.RecurringPayment.start_date')}{' '}
                <span className="normal-case text-gray-400" data-id="element-8721" data-mp="12q6wgios70bc">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <button type="button" aria-expanded={showDatePicker} aria-haspopup="dialog" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-8722" data-mp="2e2hhu4173u5r">
                <div className="flex items-center gap-2" data-id="element-8723" data-mp="2cny8sk8oo537">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-8724" data-mp="2hlzwjih20sl0" />
                  <span className={startDate ? 'text-gray-900' : 'text-gray-400'} data-id="element-8725" data-mp="359zj067c1wts">
                    {startDate ? formatDateFull(startDate) : 'Start immediately'}
                  </span>
                </div>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} data-id="element-8726" data-mp="30jj7wsqzinal" />
              </button>

              {showDatePicker && <div className="absolute z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-8727" data-mp="3t1oh636u5fin">
                  <div className="flex justify-between items-center mb-3" data-id="element-8728" data-mp="6oloxdr8lsy9">
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} data-id="element-8729" data-mp="1s4r1ru8k07z">
                      <ChevronLeftIcon size={14} data-id="element-8730" data-mp="3a86m030c8p2c" />
                    </button>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-8731" data-mp="3593gyikcyiud">
                      {calendarMonth.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric'
                  })}
                    </div>
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} data-id="element-8732" data-mp="1bpl7ov6i2y9o">
                      <ChevronRightIcon size={14} data-id="element-8733" data-mp="x0otegduop0m" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-8734" data-mp="11zs0s7haz7br">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-8735" data-mp="16scqoxx390a0">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center" data-id="element-8736" data-mp="991l7fodc2no">
                    {calendarWeeks.flatMap((week, wi) => week.map((date, di) => date ? <button key={`${wi}-${di}`} type="button" disabled={isDatePast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isDateSelected(date) ? 'bg-[#E2231A] text-white' : isDateToday(date) ? 'border border-[#E2231A] text-[#E2231A]' : isDatePast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
                  setStartDate(date);
                  setShowDatePicker(false);
                }} data-id="element-8737" data-mp="2gui3qhlnu0x2">
                            {date.getDate()}
                          </button> : <div key={`${wi}-${di}`} className="w-8 h-8" data-id="element-8738" data-mp="1a8xpadxblhzg" />))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-8739" data-mp="106lmarzpwa82">
                    <button type="button" className="flex-1 text-xs font-medium text-[#E2231A] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(null);
                  setShowDatePicker(false);
                }} data-id="element-8740" data-mp="145ayih90rlq4">
                      {t('components.RecurringPayment.start_immediately')}
                    </button>
                    <button type="button" className="flex-1 text-xs font-medium text-[#E2231A] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(new Date());
                  setShowDatePicker(false);
                }} data-id="element-8741" data-mp="7h4zqays3njr">
                      {t('components.InstallmentPayment.today')}
                    </button>
                  </div>
                </div>}
            </div>

            <div data-id="element-8742" data-mp="1vdky02052084">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8743" data-mp="1vd2ssfvi0bo2">
                {t('components.RecurringPayment.trial_period_days')}
              </label>
              <input type="number" min="0" className={inputClass} placeholder="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} data-id="element-8744" data-mp="2o2rtst87kbpo" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── BILLING CYCLE PREVIEW ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8745" data-mp="29b9iv9u7h7d6">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-8746" data-mp="36uc8qsff4lkm">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-8747" data-mp="320gh07pqzuxw">
            {t('components.RecurringPayment.billing_cycle_preview')}
          </h3>
          <span className="text-xs text-gray-400" data-id="element-8748" data-mp="3ec0nyx5iv3cc">
            {billingCycles ? `${billingCycles} cycles` : 'Ongoing'} ·{' '}
            {selectedIntervalLabel}
          </span>
        </div>

        {billingPreview.length > 0 ? <div className="max-h-80 overflow-y-auto" data-id="element-8749" data-mp="3obhq54lkez3k">
            {billingPreview.map((cycle, idx) => <div key={idx} className={`flex items-center justify-between px-6 py-3 ${idx < billingPreview.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-8750" data-mp="3sba6i3h7a9jh" data-mpi={idx}>
                <div className="flex items-center gap-3" data-id="element-8751" data-mp="5wz4lxkfrxno">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${cycle.isTrial ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-8752" data-mp="aw3cmkvmjpcf">
                    {cycle.isTrial ? 'T' : cycle.cycle}
                  </div>
                  <div data-id="element-8753" data-mp="1565czglq0n4j">
                    <p className="text-sm text-gray-900" data-id="element-8754" data-mp="155dkwfrc17j0">
                      {formatDateShort(cycle.date)}
                    </p>
                    {cycle.isTrial && <p className="text-[11px] text-blue-500" data-id="element-8755" data-mp="1f41pocub3q4r">
                        {t('components.RecurringPayment.trial_period')}{' '}
                        {trialDays} {t('components.RecurringPayment.days_free')}
                      </p>}
                  </div>
                </div>
                <span className={`text-sm font-medium ${cycle.isTrial ? 'text-blue-500' : 'text-gray-900'}`} data-id="element-8756" data-mp="hj74p67r8lhp">
                  {cycle.isTrial ? `${getTenant().currency.code} 0` : amount ? `${getTenant().currency.code} ${amount}` : '—'}
                </span>
              </div>)}
            {!billingCycles && <div className="px-6 py-3 border-t border-gray-200 bg-gray-50/50 text-center" data-id="element-8757" data-mp="2aeq62c5jaube">
                <p className="text-xs text-gray-400" data-id="element-8758" data-mp="2f56lu2wtus0x">
                  {t('components.RecurringPayment.continues_indefinitely')}
                </p>
              </div>}
          </div> : <div className="px-6 py-8 text-center" data-id="element-8759" data-mp="25eves7huk2pt">
            <p className="text-sm text-gray-500" data-id="element-8760" data-mp="25fmzuodtii1u">
              {t('components.RecurringPayment.enter_an_amount_and_billing_interval')}
            </p>
          </div>}
      </div>
    </div>;
}